import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, CalendarIcon, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { Button } from '~/components/ui/button';
import { Calendar } from '~/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import { useAppointmentsCalendarStore } from '~/stores/appointments-calendar-store';
import { StoreSelector } from './AppointmentsCalendar';
import { MyAvailabilityTab } from '~/components/shift-planning/MyAvailabilityTab';
import { ShiftPlanDraftTab } from '~/components/shift-planning/ShiftPlanDraftTab';
import { useUserStore } from '~/stores/user-store';
import { UserRole } from '~/types/server-types/UserRole';

type DaySummary =
    | { type: 'available'; startTime: string; endTime: string }
    | { type: 'blocked'; startTime: string; endTime: string; blockerType: string | null }
    | null;

const BLOCKER_TYPE_LABELS: Record<string, string> = {
    BREAK: 'Pause',
    VACATION: 'Urlaub',
    MEETING: 'Meeting',
    SICK_LEAVE: 'Krank',
    EDUCATION: 'Fortbildung',
    OTHER: 'Sonstiges',
};

function timeToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
}

function minutesToTime(minutes: number): string {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function subtractIntervals(start: number, end: number, blockerIntervals: [number, number][]): [number, number][] {
    let segments: [number, number][] = [[start, end]];
    for (const [bStart, bEnd] of blockerIntervals) {
        const next: [number, number][] = [];
        for (const [sStart, sEnd] of segments) {
            if (bEnd <= sStart || bStart >= sEnd) {
                next.push([sStart, sEnd]);
            } else {
                if (sStart < bStart) next.push([sStart, bStart]);
                if (bEnd < sEnd) next.push([bEnd, sEnd]);
            }
        }
        segments = next;
    }
    return segments.filter(([s, e]) => e > s);
}

function computeDaySummary(
    availabilities: { startTime: string; endTime: string }[],
    blockers: { startTime: string; endTime: string; type: string | null; note: string | null }[]
): DaySummary {
    if (availabilities.length === 0 && blockers.length === 0) return null;

    const absenceBlockers = blockers.filter(b => b.type === 'VACATION' || b.type === 'SICK_LEAVE');

    if (availabilities.length === 0) {
        if (absenceBlockers.length > 0) {
            const startMinutes = Math.min(...absenceBlockers.map(b => timeToMinutes(b.startTime)));
            const endMinutes = Math.max(...absenceBlockers.map(b => timeToMinutes(b.endTime)));
            return { type: 'blocked', startTime: minutesToTime(startMinutes), endTime: minutesToTime(endMinutes), blockerType: absenceBlockers[0]?.type ?? null };
        }
        if (blockers.length > 0) {
            const startMinutes = Math.min(...blockers.map(b => timeToMinutes(b.startTime)));
            const endMinutes = Math.max(...blockers.map(b => timeToMinutes(b.endTime)));
            return { type: 'blocked', startTime: minutesToTime(startMinutes), endTime: minutesToTime(endMinutes), blockerType: blockers[0]?.type ?? null };
        }
        return null;
    }

    const availStart = Math.min(...availabilities.map(a => timeToMinutes(a.startTime)));
    const availEnd = Math.max(...availabilities.map(a => timeToMinutes(a.endTime)));

    if (absenceBlockers.length === 0) {
        return { type: 'available', startTime: minutesToTime(availStart), endTime: minutesToTime(availEnd) };
    }

    const absenceIntervals: [number, number][] = absenceBlockers.map(b => [
        timeToMinutes(b.startTime),
        timeToMinutes(b.endTime),
    ]);

    const remaining = subtractIntervals(availStart, availEnd, absenceIntervals);

    if (remaining.length === 0) {
        const startMinutes = Math.min(...absenceBlockers.map(b => timeToMinutes(b.startTime)));
        const endMinutes = Math.max(...absenceBlockers.map(b => timeToMinutes(b.endTime)));
        return { type: 'blocked', startTime: minutesToTime(startMinutes), endTime: minutesToTime(endMinutes), blockerType: absenceBlockers[0]?.type ?? null };
    }

    const longest = remaining.reduce((best, seg) => (seg[1] - seg[0] > best[1] - best[0] ? seg : best), remaining[0]);
    return { type: 'available', startTime: minutesToTime(longest[0]), endTime: minutesToTime(longest[1]) };
}

const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const ShiftPlanning = () => {
    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow(state => ({ selectedStore: state.selectedStore }))
    );
    const userRole = useUserStore(state => state.user?.role);

    const [currentWeekStart, setCurrentWeekStart] = useState(() =>
        dayjs().startOf('isoWeek')
    );

    const handlePrevWeek = () => setCurrentWeekStart(prev => prev.subtract(7, 'day'));
    const handleNextWeek = () => setCurrentWeekStart(prev => prev.add(7, 'day'));
    const handleTodayClick = () => setCurrentWeekStart(dayjs().startOf('isoWeek'));

    const daysOfWeek = useMemo(
        () => Array.from({ length: 7 }, (_, i) => currentWeekStart.add(i, 'day')),
        [currentWeekStart]
    );

    const weekEndDisplay = currentWeekStart.add(6, 'day');
    const weekNumber = currentWeekStart.isoWeek();

    const { data, isLoading } = trpc.practitioners.getStoreWeeklyScheduleOverview.useQuery(
        {
            storeId: selectedStore?.id ?? 0,
            weekStart: currentWeekStart.format('YYYY-MM-DD'),
        },
        { enabled: !!selectedStore }
    );

    const groupedPractitioners = useMemo(() => {
        if (!data?.practitioners) return [];

        const groups: { categoryId: number | null; categoryName: string; practitioners: typeof data.practitioners }[] = [];
        const categoryMap = new Map<number | null, typeof data.practitioners>();

        for (const p of data.practitioners) {
            const catId = p.category?.id ?? null;
            if (!categoryMap.has(catId)) {
                categoryMap.set(catId, []);
            }
            categoryMap.get(catId)!.push(p);
        }

        for (const [catId, practitioners] of categoryMap) {
            const categoryName = practitioners[0]?.category?.name ?? 'Ohne Kategorie';
            groups.push({ categoryId: catId, categoryName, practitioners });
        }

        groups.sort((a, b) => {
            if (a.categoryId === null) return 1;
            if (b.categoryId === null) return -1;
            return a.categoryId - b.categoryId;
        });

        return groups;
    }, [data]);

    return (
        <Content>
            <div className="container mx-auto py-6">
                <h1 className="text-2xl font-bold mb-6">Schichtplanung</h1>

                <Tabs defaultValue="schichtplan" className="w-full">
                    <TabsList className="mb-6">
                        <TabsTrigger value="schichtplan">Schichtplan</TabsTrigger>
                        <TabsTrigger value="my-availability">Meine Verfügbarkeit</TabsTrigger>
                        {(userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR) && (
                            <TabsTrigger value="draft">Schichtplanungs-Entwurf</TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value="schichtplan">
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                            <StoreSelector />

                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={handlePrevWeek}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleTodayClick}>
                                    Heute
                                </Button>
                                <Button variant="outline" size="sm" onClick={handleNextWeek}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                                <span className="text-sm font-medium ml-2">
                                    KW {weekNumber}, {currentWeekStart.format('DD.MM.')} - {weekEndDisplay.format('DD.MM.YYYY')}
                                </span>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" size="sm" className="ml-2">
                                            <CalendarIcon className="h-4 w-4" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="end">
                                        <Calendar
                                            mode="single"
                                            selected={currentWeekStart.toDate()}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setCurrentWeekStart(dayjs(date).startOf('isoWeek'));
                                                }
                                            }}
                                            weekStartsOn={1}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>

                        {!selectedStore ? (
                            <div className="flex items-center justify-center h-64">
                                <p className="text-muted-foreground">Bitte wählen Sie eine Filiale aus.</p>
                            </div>
                        ) : isLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-300 overflow-auto max-h-[calc(100vh-220px)]">
                                <table className="w-full border-collapse min-w-[800px]">
                                    <thead>
                                        <tr>
                                            <th className="sticky top-0 z-10 bg-gray-100 text-left px-4 py-3 font-medium text-sm text-muted-foreground w-[180px] border-b-2 border-gray-300">
                                                Behandler
                                            </th>
                                            {daysOfWeek.map((day, i) => (
                                                <th key={i} className="sticky top-0 z-10 bg-gray-100 text-center px-2 py-3 font-medium text-sm text-muted-foreground border-b-2 border-l border-gray-300">
                                                    {DAY_LABELS[i]} {day.format('DD.MM.')}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupedPractitioners.length === 0 && (
                                            <tr>
                                                <td colSpan={8} className="text-center py-8 text-muted-foreground">
                                                    Keine Behandler in dieser Filiale gefunden.
                                                </td>
                                            </tr>
                                        )}
                                        {groupedPractitioners.map((group, groupIdx) => (
                                            <CategoryGroup
                                                key={group.categoryId ?? 'uncategorized'}
                                                group={group}
                                                isFirst={groupIdx === 0}
                                                weekStart={currentWeekStart.format('YYYY-MM-DD')}
                                                colorIndex={groupIdx}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="my-availability">
                        <MyAvailabilityTab />
                    </TabsContent>

                    {(userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR) && (
                        <TabsContent value="draft">
                            <ShiftPlanDraftTab />
                        </TabsContent>
                    )}
                </Tabs>
            </div>
        </Content>
    );
};

const CATEGORY_COLORS = [
    'bg-emerald-50',
    'bg-purple-50',
    'bg-blue-50',
    'bg-orange-100',
    'bg-amber-50',
    'bg-rose-50',
    'bg-cyan-50',
    'bg-indigo-50',
];

function CategoryGroup({ group, isFirst, weekStart, colorIndex }: {
    group: {
        categoryId: number | null;
        categoryName: string;
        practitioners: {
            id: number;
            firstName: string;
            lastName: string;
            title: string | null;
            days: {
                date: string;
                availabilities: { startTime: string; endTime: string }[];
                blockers: { startTime: string; endTime: string; type: string | null; note: string | null }[];
            }[];
        }[];
    };
    isFirst: boolean;
    weekStart: string;
    colorIndex: number;
}) {
    const bgColor = CATEGORY_COLORS[colorIndex % CATEGORY_COLORS.length];

    return (
        <>
            <tr className={!isFirst ? 'border-t-2 border-gray-300' : ''}>
                <td colSpan={8} className={`px-4 py-2 ${bgColor}`}>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.categoryName}
                    </span>
                </td>
            </tr>
            {group.practitioners.map(practitioner => (
                <PractitionerRow key={practitioner.id} practitioner={practitioner} weekStart={weekStart} bgColor={bgColor} />
            ))}
        </>
    );
}

function PractitionerRow({ practitioner, weekStart, bgColor }: {
    practitioner: {
        id: number;
        firstName: string;
        lastName: string;
        title: string | null;
        days: {
            date: string;
            availabilities: { startTime: string; endTime: string }[];
            blockers: { startTime: string; endTime: string; type: string | null; note: string | null }[];
        }[];
    };
    weekStart: string;
    bgColor: string;
}) {
    const displayName = [practitioner.title, practitioner.firstName, practitioner.lastName]
        .filter(Boolean)
        .join(' ');

    const linkBase = `/practitioner/${practitioner.id}?tab=availability&week=${weekStart}`;

    return (
        <tr className={`border-b border-gray-300 hover:brightness-95 transition-all ${bgColor}`}>
            <td className="px-4 py-2 text-sm font-medium whitespace-nowrap border-r border-gray-300">
                {displayName}
            </td>
            {practitioner.days.map((day, i) => {
                const summary = computeDaySummary(day.availabilities, day.blockers);
                return (
                    <td key={i} className="px-1 py-2 text-center border-l border-gray-300">
                        <Link to={linkBase} className="block w-full h-full min-h-[2rem]">
                            <DayCell summary={summary} />
                        </Link>
                    </td>
                );
            })}
        </tr>
    );
}

function DayCell({ summary }: { summary: DaySummary }) {
    if (!summary) return null;

    if (summary.type === 'available') {
        return (
            <div className="inline-flex flex-col items-center rounded px-2 py-1 bg-green-100 border border-green-300 text-xs">
                <span className="font-medium text-green-800">
                    {summary.startTime} - {summary.endTime}
                </span>
            </div>
        );
    }

    const typeLabel = summary.blockerType ? (BLOCKER_TYPE_LABELS[summary.blockerType] ?? summary.blockerType) : '';

    return (
        <div className="inline-flex flex-col items-center rounded px-2 py-1 bg-red-100 border border-red-300 text-xs">
            <span className="font-medium text-red-800">
                {summary.startTime} - {summary.endTime}
            </span>
            {typeLabel && (
                <span className="text-red-600 text-[10px]">{typeLabel}</span>
            )}
        </div>
    );
}

export default ShiftPlanning;
